import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Heading, Text } from "@/components/ui/typography"
import { BreathingGradient } from "@/components/ui/living-brand"
import { formatWithTooltips } from "@/lib/content/formatWithTooltips"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { sfIcons } from '@/lib/sf-icons'
import { SpotlightCard } from "@/components/ui/SpotlightCard"

type ProductsSectionProps = {
  id?: string
}

export function ProductsSection({ id }: ProductsSectionProps) {
  const { t } = useTranslation("marketing")
  
  // Product icon mapping (P1 Landing Blocks - systematic iconography)
  const productIconMap: Record<string, typeof sfIcons[keyof typeof sfIcons]> = {
    liquidationAddress: sfIcons.liquidationAddress,  // Wallet icon
    virtualAccount: sfIcons.virtualAccount,         // Building icon
    wallet: sfIcons.productWallet,                   // Wallet2 icon
    payoutRoutes: sfIcons.payoutRoutes,             // Route icon
  } as const
  
  // Products from SSOT (marketing.json)
  const productKeys = ["liquidationAddress", "virtualAccount", "wallet", "payoutRoutes"] as const
  const products = productKeys.map((key) => ({
    id: t(`products.${key}.id`),
    title: t(`products.${key}.title`),
    subtitle: t(`products.${key}.subtitle`),
    description: t(`products.${key}.description`),
    href: t(`products.${key}.href`),
    cta: t(`products.${key}.cta`),
    icon: productIconMap[key] || sfIcons.info, // Fallback to info icon
  }))
  return (
    <section
      id={id}
      className={cn(
        "relative py-12 xs:py-14 sm:py-24 overflow-hidden",
        "outline-none", // Remove any focus outline (section is not interactive)
        semanticTokens.harmony.calm.bg
      )}
      aria-labelledby="landing-products"
      tabIndex={-1} // Prevent focus outline (section is not interactive)
    >
      {/* Color Lab: Blue-Purple Mesh variant */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(at 40% 20%, hsla(217, 91%, 60%, 0.15) 0px, transparent 50%),
            radial-gradient(at 80% 0%, hsla(270, 95%, 75%, 0.12) 0px, transparent 50%),
            radial-gradient(at 0% 80%, hsla(217, 91%, 60%, 0.08) 0px, transparent 50%)
          `
        }}
      />
      {/* Living Brand: Breathing Gradient (subtle, syncs with LED heartbeat) */}
      <BreathingGradient
        variant="lavender"
        syncWithLed
        className="absolute inset-0 z-[1] pointer-events-none opacity-20"
      />
      <div className="relative z-10 mx-auto max-w-6xl space-y-6 xs:space-y-7 sm:space-y-10 md:space-y-12 px-4 xs:px-4 sm:px-6">
        {/* Canon v1.4.5: Premium accent for VIP product suite (SSOT) */}
        <div className="space-y-3 sm:space-y-4 text-center">
          <div className={cn(
            "inline-flex items-center gap-2 px-3 py-1 rounded-full font-bold uppercase tracking-wide mx-auto",
            semanticTokens.typography.captionSmall,
            semanticTokens.premium.bg,
            semanticTokens.premium.fg
          )}>
            {t("productsSection.badge")}
          </div>
          <Heading level={2} id="landing-products">
            {t("productsSection.headline")}{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {t("productsSection.headlineHighlight")}
            </span>
          </Heading>
          <Text variant="lg" color="muted" className="mx-auto max-w-3xl">
            {t("productsSection.description")}
          </Text>
        </div>

        {/* 2x2 Grid Layout: 4 products balanced in 2 rows */}
        <div className="grid grid-cols-1 gap-4 sm:gap-5 md:gap-6 md:grid-cols-2">
          {products.map((product) => (
            <SpotlightCard
              key={product.id}
              className={cn(
                // CANON: 240ms standard motion duration
                "flex h-full flex-col justify-between transition-all hover:shadow-xl hover:-translate-y-1",
                semanticTokens.motion.standard,
                "p-6 sm:p-7 md:p-8"
              )}
            >
              <div className="space-y-3 sm:space-y-4">
                <div className="space-y-3">
                  {/* Product Icon */}
                  {product.icon && (() => {
                    const Icon = product.icon
                    return (
                      <div className={cn(
                        "inline-flex items-center justify-center rounded-xl",
                        "h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12",
                        "border-2",
                        semanticTokens.border.default,
                        semanticTokens.accent.fg, // Use accent color for icons
                      )}>
                        <Icon className="h-5 w-5 sm:h-5.5 sm:w-5.5 md:h-6 md:w-6" aria-hidden="true" />
                      </div>
                    )
                  })()}
                  <div className="space-y-1">
                    <Text variant="caption" color="muted" className="uppercase tracking-wide font-medium">
                      {product.title}
                    </Text>
                    <Heading level={3}>
                      {product.subtitle}
                    </Heading>
                  </div>
                </div>
                <Text variant="sm" color="muted" className="leading-relaxed">
                  {formatWithTooltips(product.description)}
                </Text>
              </div>
              <div className="mt-6 sm:mt-7 md:mt-8 pt-5 sm:pt-6 border-t border-slate-100 dark:border-slate-800">
                <Link
                  to={product.href}
                  className={cn(
                    "inline-flex items-center gap-2",
                    // Typography Canon: Inter for UI text
                    "font-body text-ui-md font-semibold transition-colors",
                    "text-primary hover:text-primary/80",
                    "min-h-[44px]", // Touch target ≥44px
                    semanticTokens.focus.ring
                  )}
                  aria-label={`${product.cta} ${product.subtitle}`}
                >
                  {product.cta}
                  <sfIcons.externalLink className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </SpotlightCard>
          ))}
        </div>
      </div>
    </section>
  )
}
